import {consume} from "@lit/context";
import type {PropertyValues} from "lit";
import {LitElement, html, nothing, unsafeCSS} from "lit";
import {customElement} from "lit/decorators.js";
import {unsafeHTML} from "lit/directives/unsafe-html.js";
import {unsafeStatic} from "lit/static-html.js";
import type {RuntimeEnvironment} from "@common-core/web-component-runtime/runtime";

import CoxAutomotiveLogo from "../../assets/cox-automotive-logo.svg?raw";
import {BridgeBarConfigContext} from "../../config/BridgeBarConfigContext";
import type {BridgeBarConfig} from "../../config/BridgeBarConfig";
import {ROOT_TAG} from "../../globals";
import {RuntimeContext} from "../../runtime/RuntimeContext";
import type {Environment} from "../../runtime/Environment";
import {emitLogoNav} from "../../analytics";
import styles from "./CompanyLogoElement.css?inline";

const tagName = `${ROOT_TAG}-company-logo`;
export const CompanyLogo = unsafeStatic(tagName);

/**
 * Renders the Cox Automotive wordmark at the far left of the Bridge Bar,
 * linking to the environment-specific landing page. The SVG uses
 * `fill="currentColor"` so the wordmark inherits the bar's text color
 * (primary-700 in light mode, white in dark mode).
 *
 * Renders nothing until the runtime context resolves a `landingPageUrl`,
 * mirroring the legacy logo's hidden-until-ready behavior.
 */
@customElement(tagName)
export class CompanyLogoElement extends LitElement {
  static styles = unsafeCSS(styles);

  @consume({context: BridgeBarConfigContext, subscribe: true})
  private config: BridgeBarConfig | undefined;

  @consume({context: RuntimeContext, subscribe: true})
  private runtime: RuntimeEnvironment<Environment, unknown> | undefined;

  override willUpdate(_changed: PropertyValues): void {
    // Each themed element must carry `data-ids-theme-mode` on its own host
    // so Tailwind `dark:` variants resolve — `:host-context()` is not
    // supported in Firefox.
    const mode = this.config?.theme ?? "light";
    if (this.getAttribute("data-ids-theme-mode") !== mode) {
      this.setAttribute("data-ids-theme-mode", mode);
    }
  }

  override render() {
    const landingPageUrl = this.runtime?.environment?.landingPageUrl;
    if (!landingPageUrl) {
      return nothing;
    }
    return html`
      <a
        data-testid="company-logo"
        class="flex items-center text-primary-700 dark:text-white h-8"
        href=${landingPageUrl}
        aria-label="Cox Automotive"
        @click=${(e: MouseEvent) =>
          emitLogoNav(landingPageUrl, e, this.config?.getTracker)}
        @contextmenu=${(e: MouseEvent) =>
          emitLogoNav(landingPageUrl, e, this.config?.getTracker)}>
        ${unsafeHTML(CoxAutomotiveLogo)}
      </a>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [tagName]: CompanyLogoElement;
  }
}
